/**
    * UI management mixin
    * Provides functionality for managing the user interface, including toolbars, popups, and menus.
 */
export default {
    data() {
        return {

            show_welcome_screen: true,

            selected_object: null,
            selected_object_array: [],
            selected_object_ref: null,
            toolbar_expanded: false,    
            show_popup: false,
            popup_title: '',
            popup_content: '',
            
            // Templates system
            templates: [],
            show_templates_modal: false,
            loading_templates: false,
            
            // Premium templates status (manually configured)
            premium_template_status: {
                '01': false,
                '02': true,
                '03': true,
                '04': false,
                '05': false,
                '06': true,
                '07': true,
                '08': false,
                '09': false
            },
            
            // Premium modal
            is_premium_modal_open: false,
            premium_modal: {
                action: '',
                message: '',
                target_tier: 'premium' // 'premium' or 'creator_pro'
            },
            
            // Feature availability control (for future automation)
            feature_flags: {
                ai_generation: false,
                ai_character_manager: false,
                ai_translation: false,
                // Add more AI features here as they're developed
            },
            
            //menus
            show_file_menu: false,     
            show_edit_menu: false,
            show_image_menu: false,
            show_view_menu: false,
            show_about_menu: false,
            show_mobile_menu: false,
            show_user_menu: false,
            
            // Right bar collapse for mobile
            right_bar_collapsed: false,
            is_mobile_view: false,

            status_message: "Ready",              
            show_image_controls: false,
            show_balloon_controls: false,
            panel_collapsed: {},
            locked_ui: false, //for tools with focus

            // Subscription success modal (no X button, forces refresh)
            subscription_success_modal: {
                show: false,
                message: '',
                type: 'success' // success, error
            },

        };
    },

    methods: {

        deselect_all() {
            this.deselect_panel();
            this.deselect_object();
            this.deselect_subtool();
        },

        /**
         * Ensure all text nodes in a balloon group have custom_box_for_recenter attributes set
         * @param {Object} balloon_group - Konva Group representing the balloon
         */
        ensure_all_text_boxes_have_custom_box_for_recenter(balloon_group) {
            if (!balloon_group || 
                balloon_group.getAttr('object_type') !== 'balloon' || 
                !balloon_group.texts || 
                !Array.isArray(balloon_group.texts)) {
                return; // Not a merged balloon, nothing to do
            }
            
            // For each text in the balloon, ensure it has custom_box_for_recenter
            balloon_group.texts.forEach(text_node => {
                if (!text_node) return;
                
                // Check if already has proper custom box settings
                const custom_box = text_node.getAttr('custom_box_for_recenter');
                if (custom_box && typeof custom_box.width === 'number' && custom_box.width > 0) {
                    return; // This text already has proper settings
                }
                
                // Calculate appropriate dimensions for this text
                const group_scale_x = balloon_group.scaleX() || 1;
                const group_scale_y = balloon_group.scaleY() || 1;
                const text_width = text_node.width() * group_scale_x;
                const text_height = text_node.height() * group_scale_y;
                
                // Calculate position (x,y)
                const text_x = text_node.x();
                const text_y = text_node.y();
                
                // Create and set the custom box attribute
                const box_bounds = {
                    x: text_x,
                    y: text_y,
                    width: text_width,
                    height: text_height
                };
                text_node.setAttr('custom_box_for_recenter', box_bounds);
            });
        },

        toggle_toolbar() {
            this.toolbar_expanded = !this.toolbar_expanded;

            if (this.selected_object_is_balloon)
            {
                setTimeout(() => {
                    this.update_balloon_toolbar_position(this.selected_object);
                }, 400);
            }

            //this.handle_window_resize(true);
        },              




        
        show_popup_with_content(title, content, options = {}) {
            // Add icon to title if specified
            const popup_type = options.type || 'default';
            let icon_html = '';
            
            switch (popup_type) {
                case 'error':
                    icon_html = '<i data-lucide="alert-circle" class="w-5 h-5 mr-2 text-red-400"></i>';
                    break;
                case 'warning':
                    icon_html = '<div class="w-10 h-10 bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg mr-3"><i data-lucide="alert-triangle" class="w-5 h-5 text-white"></i></div>';
                    break;
                case 'success':
                    icon_html = '<i data-lucide="check-circle" class="w-5 h-5 mr-2 text-green-400"></i>';
                    break;
                case 'info':
                    icon_html = '<div class="w-10 h-10 bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg mr-3"><i data-lucide="info" class="w-5 h-5 text-white"></i></div>';
                    break;
            }
            
            this.popup_title = icon_html + title;
            this.popup_content = content;
            this.show_popup = true;
            
            this.$nextTick(() => {
                this.refresh_lucide();
                
                // Auto-focus first button if present
                const first_button = document.querySelector('#popup_overlay button');
                if (first_button) {
                    first_button.focus();
                }
            });
        },
        
        process_popup_content(content, type, options) {
            // Enhanced content wrapper with better typography and spacing
            let wrapper_classes = 'space-y-4';
            let icon_html = '';
            
            // Add type-specific styling and icons
            switch (type) {
                case 'error':
                    icon_html = '<div class="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-500/10 rounded-full"><i data-lucide="alert-circle" class="w-6 h-6 text-red-400"></i></div>';
                    break;
                case 'warning':
                    icon_html = '<div class="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-500/10 rounded-full"><i data-lucide="alert-triangle" class="w-6 h-6 text-amber-400"></i></div>';
                    break;
                case 'success':
                    icon_html = '<div class="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-500/10 rounded-full"><i data-lucide="check-circle" class="w-6 h-6 text-green-400"></i></div>';
                    break;
                case 'info':
                    icon_html = '<div class="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gray-500/10 rounded-full"><i data-lucide="info" class="w-6 h-6 text-rose-400"></i></div>';
                    break;
            }
            
            // Wrap content with enhanced styling
            return `
                <div class="${wrapper_classes}">
                    ${icon_html}
                    <div class="text-gray-100 leading-relaxed">
                        ${content}
                    </div>
                </div>
            `;
        },
        
        close_popup() {
            this.show_popup = false;
            this.popup_title = '';
            this.popup_content = '';
        },

        show_proportional_mode_help() {
            const help_content = `
                <div class="space-y-4 text-sm text-gray-300">
                    <div class="bg-gray-700 p-3 rounded-md">
                        <h4 class="font-semibold text-green-400 mb-2">
                            <i class="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></i>
                            ${this.get_t('help.proportional_mode.green_edge_handles')}
                        </h4>
                        <p>${this.get_t('help.proportional_mode.description')}</p>
                    </div>
                    
                    <div class="space-y-3">
                        <div class="border-l-4 border-gray-500 pl-3">
                            <h5 class="font-medium text-gray-400">${this.get_t('help.proportional_mode.mode_on_title')}</h5>
                            <p>${this.get_t('help.proportional_mode.mode_on_description')}</p>
                        </div>
                        
                        <div class="border-l-4 border-orange-500 pl-3">
                            <h5 class="font-medium text-orange-400">${this.get_t('help.proportional_mode.mode_off_title')}</h5>
                            <p>${this.get_t('help.proportional_mode.mode_off_description')}</p>
                        </div>
                    </div>
                    
                    <div class="bg-gray-700 p-3 rounded-md">
                        <h4 class="font-semibold text-red-400 mb-2">
                            <i class="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></i>
                            ${this.get_t('help.proportional_mode.red_vertex_handles')}
                        </h4>
                        <p>${this.get_t('help.proportional_mode.red_vertex_description')}</p>
                    </div>
                    
                    <div class="mt-4 p-3 bg-gray-900/50 rounded-md border border-gray-600">
                        <p class="text-gray-200">${this.get_t('help.proportional_mode.tip')}</p>
                    </div>
                </div>
            `;
            
            this.show_popup_with_content('Proportional Mode Guide', help_content);
        },

        update_status_message() {
            this.status_message = this.document_has_unsaved_changes 
                ? this.get_t('status.unsaved_changes') 
                : this.get_t('status.ready');
        },    

        show_new_document_dialog() {
            // Reset temporary options
            this.new_doc_options = {
                aspect_ratio_id: this.current_aspect_ratio || 'comic_portrait',
                base_size: 'medium',
                page_margin: this.page_margin,
                outline_width: this.outline_width,
                outline_color: this.outline_color,
                background_color: this.background_color,
                custom_width: this.current_aspect_ratio === 'custom' ? this.canvas_width : 800,
                custom_height: this.current_aspect_ratio === 'custom' ? this.canvas_height : 1200
            };

            // Calculate initial dimensions for display
            let initial_dimensions = { width: 800, height: 1200 };
            if (this.new_doc_options.aspect_ratio_id !== 'custom') {
                initial_dimensions = this.calculate_dimensions_from_aspect_ratio(
                    this.new_doc_options.aspect_ratio_id, 
                    this.new_doc_options.base_size
                );
            } else {
                initial_dimensions.width = this.new_doc_options.custom_width;
                initial_dimensions.height = this.new_doc_options.custom_height;
            }


            // Build the HTML for the configuration popup with aspect ratio selector
            const dialog_html = `
                <!-- Tab Navigation -->
                <div class="mb-6">
                    <nav class="flex space-x-1 bg-gray-700/30 rounded-lg p-1">
                        <button id="tab_blank" class="flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors bg-rose-600 text-white">
                            ${this.get_t('new_document.blank_document')}
                        </button>
                        <button id="tab_templates" class="flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors text-gray-400 hover:text-white">
                            ${this.get_t('welcome.templates')}
                        </button>
                    </nav>
                </div>

                <!-- Blank Document Panel -->
                <div id="panel_blank" class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Left Column: Controls -->
                    <div class="space-y-4">
                        <!-- Quality message -->
                        <div class="bg-gradient-to-r from-gray-900/20 to-purple-900/20 border border-gray-600/30 rounded-lg p-3">
                            <div class="flex items-center gap-3 mb-2">
                                <i data-lucide="info" class="w-5 h-5 text-blue-400 flex-shrink-0"></i>
                                <span class="text-sm font-medium text-gray-300">${this.get_t('new_document.quality_message')}</span>
                            </div>
                        </div>

                        <!-- Aspect Ratio Selector -->
                        <div>
                            <label class="block text-sm font-medium mb-1">${this.get_t('new_document.aspect_ratio')}</label>
                            <select id="new_doc_aspect_ratio" class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-rose-500">
                                ${this.aspect_ratios.map(ratio => {
                                    const isCustom = ratio.id === 'custom';
                                    // Always enable the option, but handle restriction on selection
                                    return `<option value="${ratio.id}" ${ratio.id === this.new_doc_options.aspect_ratio_id ? 'selected' : ''}>${ratio.name}${isCustom && !this.user_data.is_premium ? ' ✨' : ''}</option>`;
                                }).join('')}
                            </select>
                        </div>

                        <!-- Custom Dimensions (Only for premium users and custom aspect ratio) -->
                        <div id="custom_dimensions_container" class="space-y-3 border border-gray-700 p-3 rounded-md" style="display: ${this.new_doc_options.aspect_ratio_id === 'custom' ? 'block' : 'none'}">
                            ${!this.user_data.is_premium ? `
                                <div class="bg-amber-900/20 border border-amber-600/30 rounded-lg p-3 mb-3">
                                    <div class="flex items-center gap-2">
                                        <i data-lucide="lock" class="w-4 h-4 text-amber-400"></i>
                                        <span class="text-sm text-amber-200">${this.get_t('new_document.upgrade_for_custom')}</span>
                                    </div>
                                </div>
                            ` : `
                                <p class="text-xs text-amber-400 mb-2"><i data-lucide="star" class="inline-block w-4 h-4 mr-1"></i> ${this.get_t('new_document.premium_custom_size')}</p>
                            `}
                            <div>
                                <label class="block text-sm mb-1">${this.get_t('new_document.width')}</label>
                                <input type="number" id="new_doc_custom_width" min="800" max="2500" step="50"
                                       value="${initial_dimensions.width}"
                                       class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                       ${!this.user_data.is_premium ? 'disabled' : ''}>
                                <p class="text-xs text-gray-400 mt-1">${this.get_t('new_document.current_max', { max: this.user_data.is_premium ? '2500px' : '1280px' })}</p>
                            </div>
                            <div>
                                <label class="block text-sm mb-1">${this.get_t('new_document.height')}</label>
                                <input type="number" id="new_doc_custom_height" min="800" max="2500" step="50"
                                       value="${initial_dimensions.height}"
                                       class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                       ${!this.user_data.is_premium ? 'disabled' : ''}>
                                <p class="text-xs text-gray-400 mt-1">${this.get_t('new_document.current_max', { max: this.user_data.is_premium ? '2500px' : '1280px' })}</p>
                            </div>
                        </div>

                        <!-- Page Margin -->
                        <div>
                            <div class="flex justify-between mb-1">
                                <label class="text-s">${this.get_t('new_document.page_margin')}</label>
                                <span id="new_doc_margin_value" class="text-s">${this.new_doc_options.page_margin}px</span>
                            </div>
                            <input id="new_doc_margin" type="range" min="0" max="100" step="5" value="${this.new_doc_options.page_margin}" class="w-full accent-rose-600">
                        </div>

                        <!-- Outline Width -->
                        <div>
                            <div class="flex justify-between mb-1">
                                <label class="text-s">${this.get_t('new_document.outline_width')}</label>
                                <span id="new_doc_outline_value" class="text-s">${this.new_doc_options.outline_width}px</span>
                            </div>
                            <input id="new_doc_outline" type="range" min="0" max="50" step="1" value="${this.new_doc_options.outline_width}" class="w-full accent-rose-600">
                        </div>

                        <!-- Outline Color -->
                        <div>
                            <div class="flex justify-between items-center mb-1">
                                <label class="text-s">${this.get_t('new_document.outline_color')}</label>
                                <input id="new_doc_outline_color" type="color" value="${this.new_doc_options.outline_color}" class="w-10 h-6 rounded-md cursor-pointer border border-gray-600">
                            </div>
                        </div>

                        <!-- Background Color -->
                        <div>
                            <div class="flex justify-between items-center mb-1">
                                <label class="text-s">${this.get_t('new_document.background_color')}</label>
                                <input id="new_doc_bgcolor" type="color" value="${this.new_doc_options.background_color}" class="w-10 h-6 rounded-md cursor-pointer border border-gray-600">
                            </div>
                        </div>
                    </div>

                    <!-- Right Column: Preview -->
                    <div class="flex flex-col">
                        <label class="block text-s mb-2 text-center">${this.get_t('new_document.preview')}</label>
                        <!-- Removed bg-gray-900 from this container -->
                        <div class="flex-1 flex items-center justify-center rounded-md border border-gray-700 p-4 overflow-hidden" style="background-color: #00000069;">
                            <div id="preview_area" class="relative w-full h-full">
                                <!-- The background color is now solely controlled by the style attribute -->
                                <div id="preview_canvas" class="absolute inset-0 !border-black" style="background-color: ${this.new_doc_options.background_color};">
                                    <div id="preview_margin_box" class="absolute border-black">
                                        <!-- This inner div is optional, kept for structure -->
                                        <div id="preview_content_box" class="absolute inset-0" style="background-color: #fff;"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Templates Panel -->
                <div id="panel_templates" class="hidden">
                    <div id="templates_loading" class="flex items-center justify-center py-12">
                        <div class="text-center">
                            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto mb-4"></div>
                            <p class="text-gray-400">${this.get_t('status.loading')}...</p>
                        </div>
                    </div>
                    
                    <div id="templates_grid" class="hidden grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-80 overflow-y-auto pr-2">
                        <!-- Templates will be loaded here dynamically -->
                    </div>
                    
                    <div id="templates_empty" class="hidden flex items-center justify-center py-12">
                        <div class="text-center">
                            <i data-lucide="layout-template" class="w-12 h-12 text-gray-500 mx-auto mb-4"></i>
                            <h3 class="text-lg font-semibold text-gray-400 mb-2">${this.get_t('welcome.no_templates')}</h3>
                            <p class="text-gray-500">${this.get_t('welcome.templates_desc')}</p>
                        </div>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="flex justify-end gap-4 mt-6">
                    <button id="create_new_doc_confirm" class="px-4 py-2 bg-rose-600 text-white rounded hover:bg-rose-700">${this.get_t('new_document.create')}</button>
                    <button id="create_new_doc_cancel" class="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">${this.get_t('actions.cancel')}</button>
                </div>
            `;

            this.show_popup_with_content(this.get_t('new_document.title'), dialog_html);

            // Add event listeners and update preview logic
            this.$nextTick(() => {
                this.refresh_lucide();

                // Tab switching logic
                const tab_blank = document.getElementById('tab_blank');
                const tab_templates = document.getElementById('tab_templates');
                const panel_blank = document.getElementById('panel_blank');
                const panel_templates = document.getElementById('panel_templates');

                const switch_to_blank = () => {
                    tab_blank.className = 'flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors bg-rose-600 text-white';
                    tab_templates.className = 'flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors text-gray-400 hover:text-white';
                    panel_blank.style.display = 'grid';
                    panel_templates.style.display = 'none';
                };

                const switch_to_templates = () => {
                    tab_blank.className = 'flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors text-gray-400 hover:text-white';
                    tab_templates.className = 'flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors bg-rose-600 text-white';
                    panel_blank.style.display = 'none';
                    panel_templates.style.display = 'block';
                    this.load_templates_in_dialog();
                };

                if (tab_blank && tab_templates) {
                    tab_blank.onclick = switch_to_blank;
                    tab_templates.onclick = switch_to_templates;
                }

                const aspect_ratio_select = document.getElementById('new_doc_aspect_ratio');
                const custom_dims_container = document.getElementById('custom_dimensions_container');
                const custom_width_input = document.getElementById('new_doc_custom_width');
                const custom_height_input = document.getElementById('new_doc_custom_height');
                const margin_slider = document.getElementById('new_doc_margin');
                const margin_value_span = document.getElementById('new_doc_margin_value');
                const outline_slider = document.getElementById('new_doc_outline');
                const outline_value_span = document.getElementById('new_doc_outline_value');
                const outline_color_input = document.getElementById('new_doc_outline_color');
                const bgcolor_input = document.getElementById('new_doc_bgcolor');
                const confirm_btn = document.getElementById('create_new_doc_confirm');
                const cancel_btn = document.getElementById('create_new_doc_cancel');

                const update_preview = () => this.update_new_doc_preview();

                // Handle aspect ratio changes
                if (aspect_ratio_select) {
                    aspect_ratio_select.onchange = (e) => {
                        const selectedValue = e.target.value;
                        const is_custom = selectedValue === 'custom';
                        
                        // Check premium access for custom size
                        if (is_custom && !this.user_data.is_premium) {
                            // Reset to previous value and show premium modal
                            aspect_ratio_select.value = this.new_doc_options.aspect_ratio_id;
                            this.show_premium_modal('custom_size', this.get_t('premium.custom_size_limit'));
                            return;
                        }
                        
                        this.new_doc_options.aspect_ratio_id = selectedValue;
                        
                        // Show/hide custom dimensions
                        if (custom_dims_container) {
                            custom_dims_container.style.display = is_custom ? 'block' : 'none';
                        }
                        
                        // Update input values when switching from custom
                        if (!is_custom) {
                            const new_dims = this.calculate_dimensions_from_aspect_ratio(
                                e.target.value, 
                                this.new_doc_options.base_size
                            );
                            if (custom_width_input) custom_width_input.value = new_dims.width;
                            if (custom_height_input) custom_height_input.value = new_dims.height;
                        }
                        
                        update_preview();
                    };
                }

                // Handle custom dimension inputs
                if (custom_width_input) {
                    custom_width_input.oninput = (e) => {
                        if (this.new_doc_options.aspect_ratio_id === 'custom') {
                            this.new_doc_options.custom_width = parseInt(e.target.value, 10) || 800;
                            update_preview();
                        }
                    };
                }
                
                if (custom_height_input) {
                    custom_height_input.oninput = (e) => {
                        if (this.new_doc_options.aspect_ratio_id === 'custom') {
                            this.new_doc_options.custom_height = parseInt(e.target.value, 10) || 1200;
                            update_preview();
                        }
                    };
                }
                if (margin_slider) {
                    margin_slider.oninput = (e) => {
                        this.new_doc_options.page_margin = parseInt(e.target.value, 10);
                        if (margin_value_span) margin_value_span.textContent = `${this.new_doc_options.page_margin}px`;
                        update_preview();
                    };
                }
                if (outline_slider) {
                    outline_slider.oninput = (e) => {
                        this.new_doc_options.outline_width = parseInt(e.target.value, 10);
                        if (outline_value_span) outline_value_span.textContent = `${this.new_doc_options.outline_width}px`;
                        update_preview();
                    };
                }
                if (outline_color_input) { // Add listener for outline color
                    outline_color_input.oninput = (e) => {
                        this.new_doc_options.outline_color = e.target.value;
                        update_preview();
                    };
                }
                if (bgcolor_input) {
                    bgcolor_input.oninput = (e) => {
                        this.new_doc_options.background_color = e.target.value;
                        update_preview();
                    };
                }

                // Handle confirm/cancel actions
                if (confirm_btn) {
                    confirm_btn.onclick = () => {
                        // Validation for custom dimensions
                        if (this.new_doc_options.aspect_ratio_id === 'custom') {
                            if (!this.user_data.is_premium) {
                                this.show_error_message(
                                    this.get_t('error.title'), 
                                    this.get_t('new_document.upgrade_for_custom')
                                );
                                return;
                            }
                            
                            const w = parseInt(custom_width_input.value, 10) || 800;
                            const h = parseInt(custom_height_input.value, 10) || 1200;
                            const max_dim = this.user_data.is_premium ? 4000 : 1280;
                            
                            this.new_doc_options.custom_width = w;
                            this.new_doc_options.custom_height = h;
                            
                            if (!this.validate_custom_dimensions(w, h)) {
                                this.show_error_message(
                                    this.get_t('error.title'), 
                                    this.get_t('new_document.dimension_limit_exceeded')
                                );
                                return;
                            }
                        }
                        
                        this.close_popup();
                        this.create_new_document_with_options();
                    };
                }
                
                if (cancel_btn) {
                    cancel_btn.onclick = () => this.close_popup();
                }

                // Initial preview update
                setTimeout(update_preview, 0);
            });
        },

        redraw(){
            const global_panel = this.panels.find(panel => panel.getAttr('is_global'));
            if (global_panel) {
                global_panel.moveToTop();
            }

            this.$forceUpdate();
            this.panels_layer.batchDraw();
            this.ui_layer.batchDraw();
        },

        update_blend_mode() {
            if (!this.selected_layer || !this.selected_layer.node || this.selected_layer.locked) return;
            
            // Aplicar el blend mode seleccionado
            this.selected_layer.node.globalCompositeOperation(this.selected_blend_mode);
            this.redraw();
            this.push_to_history('change_blend_mode');
        },

        set_object_blend_mode_and_opacity(object)
        {
            if (object) {
                // Get blend mode with fallback to 'normal'
                const blend_mode = object.globalCompositeOperation();
                this.selected_blend_mode = blend_mode || 'normal';
                
                // Get opacity with fallback to 100%
                const opacity = Math.round((object.opacity() || 1) * 100);
                this.selected_opacity = opacity;
                this.selected_opacity_text = opacity + '%';
            }
            else {
                this.selected_blend_mode = 'normal';
                this.selected_opacity = 100;
                this.selected_opacity_text = '100%';
            }
        },
        
        update_opacity() {
            if (!this.selected_layer || !this.selected_layer.node || this.selected_layer.locked) return;
            
            // Actualizar campo de texto para que coincida con el slider
            this.selected_opacity_text = this.selected_opacity + '%';
            
            // Aplicar la opacidad seleccionada (convertir de porcentaje a decimal)
            this.selected_layer.node.opacity(this.selected_opacity / 100);
            this.redraw();
        },

        update_opacity_from_text() {
            if (!this.selected_layer || !this.selected_layer.node || this.selected_layer.locked) return;
            
            // Extraer solo los números del texto
            let opacity_text = this.selected_opacity_text.replace(/[^0-9]/g, '');
            let opacity = parseInt(opacity_text);
            
            // Aplicar validación
            if (isNaN(opacity)) opacity = 100;
            opacity = Math.min(100, Math.max(0, opacity));
            
            // Actualizar los controles con valor válido
            this.selected_opacity = opacity;
            this.selected_opacity_text = opacity + '%';
            
            // Aplicar a la capa
            this.selected_layer.node.opacity(opacity / 100);
            this.redraw();
            this.push_to_history('change_opacity');
        },
        
        validate_opacity_text() {
            if (!this.selected_layer || this.selected_layer.locked) return;
            
            // Asegurar que el texto de entrada tenga un valor válido y el símbolo %
            let opacity_text = this.selected_opacity_text.replace(/[^0-9]/g, '');
            let opacity = parseInt(opacity_text);
            
            if (isNaN(opacity)) opacity = 100;
            opacity = Math.min(100, Math.max(0, opacity));
            
            this.selected_opacity = opacity;
            this.selected_opacity_text = opacity + '%';
        },

        validate_custom_dimensions(width, height) {
            // Check absolute maximum dimensions for all users (performance limit)
            const max_dimension = Math.max(width, height);
            if (max_dimension > 2500) {
                this.show_error_message(
                    this.get_t('error.title'),
                    this.get_t('new_document.dimension_limit_exceeded')
                );
                return false;
            }
            
            // Premium users can use dimensions up to 2500px
            if (this.user_data.is_premium) return true;
            
            // Free users are limited to dimensions up to 1200px
            if (max_dimension > 1200) {
                this.show_premium_modal('custom_size', this.get_t('premium.custom_size_limit'));
                return false;
            }
            
            return true;
        },
        
        toggle_opacity_slider(event) {
            if (this.selected_layer && this.selected_layer.locked) return;
            
            // Stop propagation to prevent click_away from firing immediately
            if (event) {
                event.stopPropagation();
            }
            
            // Toggle slider visibility
            this.show_opacity_slider = !this.show_opacity_slider;
            this.refresh_lucide();
        },
          hide_opacity_slider() {
            this.show_opacity_slider = false;
        },

        filter_opacity_input(event) {
            // Only allow numbers and % symbol
            const value = event.target.value;
            const filtered_value = value.replace(/[^0-9%]/g, '');
            
            // Make sure there's only one % and it's at the end
            const numbers_only = filtered_value.replace(/%/g, '');
            this.selected_opacity_text = numbers_only + '%';
        },

        show_preferences() {
            // Load current preferences from database
            this.load_preferences();
            this.show_preferences_popup = true;
            this.active_preferences_category = 'interface';
            
            // Give time for the DOM to update
            this.$nextTick(() => {
                this.scroll_to_category('interface');
            });
        },
        
        close_preferences_popup() {
            this.show_preferences_popup = false;
        },
        
        scroll_to_category(category_id) {
            this.active_preferences_category = category_id;
            
            // No necesitamos hacer scroll, simplemente mostramos/ocultamos secciones
            this.$nextTick(() => {
                // Ocultamos todas las secciones primero
                const all_sections = document.querySelectorAll('[id^="pref_"]');
                all_sections.forEach(section => {
                    section.style.display = 'none';
                });
                
                // Mostramos solo la sección seleccionada
                const selected_section = document.getElementById('pref_' + category_id);
                if (selected_section) {
                    selected_section.style.display = 'block';
                }
            });
        },

        show_error_message(title, message) {
            // Use the new error popup system for consistency
            this.show_error_popup(title, message);
        },

        // Convenience methods for different popup types
        show_error_popup(title, message) {
            const content = `<div class="text-center"><p class="text-lg">${message}</p></div>
                <div class="flex justify-center mt-6">
                    <button id="error_dismiss" class="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors duration-200 font-medium">
                        ${this.get_t('actions.ok')}
                    </button>
                </div>`;
            
            this.show_popup_with_content(title, content, { type: 'error' });
            
            this.$nextTick(() => {
                const dismiss_btn = document.getElementById('error_dismiss');
                if (dismiss_btn) {
                    dismiss_btn.onclick = () => this.close_popup();
                }
            });
        },
        
        show_warning_popup(title, message) {
            const content = `<div class="text-center"><p class="text-lg">${message}</p></div>
                <div class="flex justify-center mt-6">
                    <button id="warning_dismiss" class="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors duration-200 font-medium">
                        ${this.get_t('actions.ok')}
                    </button>
                </div>`;
            
            this.show_popup_with_content(title, content, { type: 'warning' });
            
            this.$nextTick(() => {
                const dismiss_btn = document.getElementById('warning_dismiss');
                if (dismiss_btn) {
                    dismiss_btn.onclick = () => this.close_popup();
                }
            });
        },
        
        show_confirmation_popup(title, message, on_confirm, on_cancel = null) {
            const content = `<div class="text-center"><p class="text-lg">${message}</p></div>
                <div class="flex justify-center gap-4 mt-6">
                    <button id="confirm_ok" class="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors duration-200 font-medium">
                        ${this.get_t('actions.confirm')}
                    </button>
                    <button id="confirm_cancel" class="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium">
                        ${this.get_t('actions.cancel')}
                    </button>
                </div>`;
            
            this.show_popup_with_content(title, content, { type: 'warning' });
            
            this.$nextTick(() => {
                const ok_btn = document.getElementById('confirm_ok');
                const cancel_btn = document.getElementById('confirm_cancel');
                
                if (ok_btn) {
                    ok_btn.onclick = () => {
                        this.close_popup();
                        if (on_confirm) on_confirm();
                    };
                }
                
                if (cancel_btn) {
                    cancel_btn.onclick = () => {
                        this.close_popup();
                        if (on_cancel) on_cancel();
                    };
                }
            });
        },

        show_download_tool_confirmation() {
            const title = this.get_t('download_tool.title');
            const message = this.get_t('download_tool.message');
            
            this.show_confirmation_popup(title, message, () => {
                // Download the installer
                const link = document.createElement('a');
                link.href = './downloads/mkc_installer.exe';
                link.download = 'mkc_installer.exe';
                link.click();
            });
        },

        toggle_panel_collapse(panel_id) {
            if (!this.panel_collapsed) this.panel_collapsed = {}; // Ensure object exists
            
            // Toggle the panel state immediately
            const new_state = !this.panel_collapsed[panel_id];
            this.panel_collapsed[panel_id] = new_state;
            
            // Get the panel content element
            const panel_content = document.querySelector(`[data-panel-id="${panel_id}"]`);
            if (!panel_content) return;
            
            // Apply or remove the open class immediately
            if (new_state) {
                // Panel is now collapsed, remove open class
                panel_content.classList.remove('open');
            } else {
                // Panel is now open, add open class
                panel_content.classList.add('open');
            }
            
            //! Not saving panel collapse state. discarded
            // Ensure preferences structure exists and save
            // if (!this.preferences.ui) this.preferences.ui = {};
            // if (!this.preferences.ui.panel_collapsed) this.preferences.ui.panel_collapsed = {};
            // this.preferences.ui.panel_collapsed[panel_id] = new_state;
            // this.save_preferences(true); // Silent UI save to avoid registration modal
        },

        is_panel_collapsed(panel_id) {
            return !!(this.panel_collapsed && this.panel_collapsed[panel_id]);
        },

        /**
         * Locks UI to prevent interactions outside specific mode
         * @param {boolean} lock_state - Whether to lock or unlock the UI
         * @param {string} lock_reason - Reason for the lock, used for debugging and condition checks
         */
        lock_ui(lock_state, lock_reason = '') {
            this.locked_ui = lock_state;
            this.locked_ui_reason = lock_reason || null;
            
            if (lock_state) {
                // Add class to main container for visual indication if needed
                const container = this.stage && this.stage.container();
                if (container) {
                    container.classList.add('ui-locked');
                }
            } else {
                // Remove class if it was added
                const container = this.stage && this.stage.container();
                if (container) {
                    container.classList.remove('ui-locked');
                }
                this.locked_ui_reason = null;
            }
        },
        
        /**
         * Check if UI can receive interaction based on lock state
         * @param {string} action_type - Type of action being attempted
         * @param {Object} excluded_contexts - Contexts where lock should be ignored
         * @returns {boolean} - True if the action should proceed, false if blocked
         */
        can_interact(action_type, excluded_contexts = {}) {
            if (!this.locked_ui) return true;
            
            // Special case: if we're in balloon edit mode, only allow specific actions
            if (this.locked_ui_reason === 'balloon_edit_mode') {
                // Always allow the exit_balloon_edit_mode action
                if (action_type === 'exit_balloon_edit_mode') return true;
                
                // Allow balloon node specific actions
                if (['node_drag', 'node_click', 'node_contextmenu', 'add_node'].includes(action_type)) return true;
                
                // Allow interaction with the specific balloon being edited
                if (action_type === 'balloon_interaction' && 
                    excluded_contexts.balloon && 
                    this.editing_balloon === excluded_contexts.balloon) {
                    return true;
                }
                
                // Block everything else
                return false;
            }
            
            return true; // Default to allowing if lock reason is unknown
        },
        
        reset_text_position() {
            if (!this.selected_object || this.selected_object.getAttr('object_type') !== 'balloon') return;
            const balloon_group = this.selected_object;

            // 1. Exit adjustment mode if active (this handles cleanup)
            if (this.text_editing.adjust_mode) {
                this.exit_text_adjust_mode(); // Call the dedicated exit function
            }
            // Ensure flag is off, exit_text_adjust_mode should do this, but belt-and-suspenders
            this.text_editing.adjust_mode = false;

            // 2. Remove custom position flag and attributes - handle both merged and standard balloons
            if (balloon_group.texts && Array.isArray(balloon_group.texts)) {
                // For merged balloons, reset each text node's custom_box_for_recenter
                balloon_group.texts.forEach(text_node => {
                    if (text_node) {
                        text_node.setAttr('custom_box_for_recenter', undefined);
                    }
                });
            } else {
                // For standard balloons, reset group attributes
                balloon_group.setAttr('custom_text_position', false);
                balloon_group.setAttr('custom_text_box_x', undefined);
                balloon_group.setAttr('custom_text_box_y', undefined);
                balloon_group.setAttr('custom_text_box_width', undefined);
                balloon_group.setAttr('custom_text_box_height', undefined);
            }

            // 3. Recenter text (will now use shape bounds because flag is false)
            this.recenter_balloon_text(balloon_group);

            // 4. Push history
            this.push_to_history('reset_balloon_text_position');

            // 5. Force redraw
            this.redraw();
        },
          // Method to update text based on the adjustment box
        _update_text_from_adjust_box(balloon, adjust_box) {
            if (!balloon || !adjust_box) return;

            let text_node;
            const group = balloon;
            
            // Get the appropriate text node for the adjustment
            if (balloon.texts && Array.isArray(balloon.texts)) {
                const text_index = this.active_text_index_in_merged_balloon !== null ? 
                    this.active_text_index_in_merged_balloon : 0;
                text_node = balloon.texts[text_index];
                
                // Store custom box on the text node itself for merged balloons
                // This needs to be a new object each time to ensure proper updates during transformations
                text_node.setAttr('custom_box_for_recenter', {
                    x: adjust_box.x(),
                    y: adjust_box.y(),
                    width: adjust_box.width() * adjust_box.scaleX(),
                    height: adjust_box.height() * adjust_box.scaleY()
                });
            } else {
                text_node = balloon.text;
            }
            
            if (!text_node) return;

            // Get group scale for inverse scaling
            const group_scale_x = group.scaleX() || 1;
            const group_scale_y = group.scaleY() || 1;
            if (group_scale_x === 0 || group_scale_y === 0) return;
            const inverse_scale_x = 1 / group_scale_x;
            const inverse_scale_y = 1 / group_scale_y;

            // Apply inverse scale
            text_node.scaleX(inverse_scale_x);
            text_node.scaleY(inverse_scale_y);

            // Get adjust_box bounds (already relative to group if added to group)
            const box_bounds = {
                x: adjust_box.x(),
                y: adjust_box.y(),
                width: adjust_box.width() * adjust_box.scaleX(), // Use scaled width
                height: adjust_box.height() * adjust_box.scaleY(), // Use scaled height
            };

            // Calculate target visual width (80% of box width)
            const padding_allowance = 0.80;
            const target_visual_width = box_bounds.width * padding_allowance;

            // Calculate required UN-SCALED width
            const required_unscaled_width = target_visual_width / inverse_scale_x;

            // Set text width
            text_node.width(required_unscaled_width);

            // Get text height AFTER setting width
            const text_height = text_node.height();

            // Calculate center position within the adjust_box
            const center_x = box_bounds.x + box_bounds.width / 2;
            const center_y = box_bounds.y + box_bounds.height / 2;

            // Position the text node
            text_node.position({ x: center_x, y: center_y });

            // Set offset for centering
            text_node.offsetX(required_unscaled_width / 2);
            text_node.offsetY(text_height / 2);

            // Redraw layer containing the balloon
            const layer = group.getLayer();
            if (layer) layer.batchDraw();
        },
        
        // Renamed: This function now ONLY ENTERS the adjust mode
        enter_text_adjust_mode() {
            //exit fron any other adjust mode
            if (this.text_editing.adjust_mode) {
                this.exit_text_adjust_mode();
            }
            this.hide_transformer();

            if (this.text_editing.adjust_mode || !this.text_editing.active || !this.selected_object || this.selected_object.getAttr('object_type') !== 'balloon') {
                if (this.text_transformer) {
                    this.text_transformer.visible(true);
                    this.ui_layer.batchDraw();
                }
                return;
            }

            const balloon_group = this.selected_object;
            const shape_node = balloon_group.shape;
            
            // Handle text selection in merged balloons or standard balloons
            let text_node;
            if (balloon_group.texts && Array.isArray(balloon_group.texts)) {
                const text_index = this.active_text_index_in_merged_balloon !== null ? 
                    this.active_text_index_in_merged_balloon : 0;
                text_node = balloon_group.texts[text_index];
            } else {
                text_node = balloon_group.text;
            }

            if (!shape_node || !text_node) {
                console.error("Balloon shape or text node not found.");
                return;
            }

            this.text_editing.adjust_mode = true;

            let initial_box;

            // --- Cambios aquí: usar siempre la caja personalizada si existe ---
            const custom_box = text_node.getAttr && text_node.getAttr('custom_box_for_recenter');
            if (custom_box && typeof custom_box.width === 'number' && custom_box.width > 0) {
                initial_box = {
                    x: custom_box.x,
                    y: custom_box.y,
                    width: custom_box.width,
                    height: custom_box.height
                };
            } else {
                const has_custom_dims = balloon_group.getAttr('custom_text_position') === true &&
                    typeof balloon_group.getAttr('custom_text_box_width') === 'number';

                if (has_custom_dims) {
                    initial_box = {
                        x: balloon_group.getAttr('custom_text_box_x') || 0,
                        y: balloon_group.getAttr('custom_text_box_y') || 0,
                        width: balloon_group.getAttr('custom_text_box_width'),
                        height: balloon_group.getAttr('custom_text_box_height') || shape_node.getClientRect({ relativeTo: balloon_group }).height,
                    };
                    if (initial_box.width <= 0) {
                        initial_box = shape_node.getClientRect({ relativeTo: balloon_group });
                    }
                } else {
                    initial_box = shape_node.getClientRect({ relativeTo: balloon_group });
                }
            }

            if (this._text_adjust_box) this._text_adjust_box.destroy();
            this._text_adjust_box = new Konva.Rect({
                x: initial_box.x,
                y: initial_box.y,
                width: initial_box.width,
                height: initial_box.height,
                draggable: true,
                name: 'text_adjust_box',
                visible: false,
                listening: true,
            });
            balloon_group.add(this._text_adjust_box);

            if (this.text_transformer) this.text_transformer.destroy();
            this.text_transformer = new Konva.Transformer({
                nodes: [this._text_adjust_box],
                enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right', 'top-center', 'bottom-center'],
                rotateEnabled: false,
                keepRatio: false,
                shouldOverdrawWholeArea: false,
                borderStroke: '#FF00FF',
                borderStrokeWidth: 1,
                borderDash: [4, 4],
                anchorStroke: '#FF00FF',
                anchorFill: '#FFFFFF',
                anchorSize: 10,
                anchorCornerRadius: 0,
            });
            this.ui_layer.add(this.text_transformer);
            this.text_transformer.moveToTop();

            balloon_group.setAttr('custom_text_position', true);

            this._text_adjust_box.off('.textadjust');
            const update_handler = () => {
                this._update_text_from_adjust_box(balloon_group, this._text_adjust_box);
            };
            this._text_adjust_box.on('transform.textadjust dragmove.textadjust', update_handler);

            const history_handler = () => {
                this.push_to_history('adjust_balloon_text_box');
            };
            this._text_adjust_box.on('dragend.textadjust transformend.textadjust', history_handler);

            this._update_text_from_adjust_box(balloon_group, this._text_adjust_box);

            balloon_group.getLayer()?.batchDraw();
            this.ui_layer.batchDraw();
        },

        // New function to handle exiting the mode
        exit_text_adjust_mode() {
            // Only run if currently in adjust mode
            if (!this.text_editing.adjust_mode) {
                // Ensure flag is false even if we bail early
                this.text_editing.adjust_mode = false;
                return;
            }

            const balloon_group = this.selected_object;

            // --- Exit adjustment mode ---            // 1. Store final box dimensions if the box exists
            if (this._text_adjust_box) {
                // Ensure scale is applied before getting dimensions
                const scaleX = this._text_adjust_box.scaleX() || 1;
                const scaleY = this._text_adjust_box.scaleY() || 1;
                const final_width = this._text_adjust_box.width() * scaleX;
                const final_height = this._text_adjust_box.height() * scaleY;
                
                // Store dimensions based on whether this is a merged balloon or not
                if (balloon_group.texts && Array.isArray(balloon_group.texts)) {
                    // For merged balloons, store dimensions on the active text node
                    const text_index = this.active_text_index_in_merged_balloon !== null ? 
                        this.active_text_index_in_merged_balloon : 0;
                    const active_text_node = balloon_group.texts[text_index];
                    
                    if (active_text_node) {
                        const box_bounds = {
                            x: this._text_adjust_box.x(),
                            y: this._text_adjust_box.y(),
                            width: final_width,
                            height: final_height
                        };
                        active_text_node.setAttr('custom_box_for_recenter', box_bounds);
                    }
                } else {
                    // For standard balloons, store dimensions on the balloon group
                    balloon_group.setAttr('custom_text_box_x', this._text_adjust_box.x());
                    balloon_group.setAttr('custom_text_box_y', this._text_adjust_box.y());
                    balloon_group.setAttr('custom_text_box_width', final_width);
                    balloon_group.setAttr('custom_text_box_height', final_height);
                    balloon_group.setAttr('custom_text_position', true);
                }

                // Clean up listeners and destroy the box
                this._text_adjust_box.off('.textadjust');
                this._text_adjust_box.destroy();
                this._text_adjust_box = null;
            } else {
                 // If box doesn't exist but we are exiting, ensure flag is set correctly
                 // This might happen if deselect is called rapidly after entering mode
                 console.warn("Exiting text adjust mode, but _text_adjust_box was not found.");
            }


            // 2. Destroy transformer
            if (this.text_transformer) {
                this.text_transformer.destroy();
                this.text_transformer = null;
            }

            // 3. Re-enable balloon group dragging if the main tool is 'select'
            // if (this.active_tool === 'select') {
            //      balloon_group.draggable(true);
            // }

            // 4. Update state flag
            this.text_editing.adjust_mode = false;

            // 5. Ensure text is updated one last time based on saved attributes
            //    (recenter_balloon_text already handles using custom attributes if flag is true)
            this.recenter_balloon_text(balloon_group);

            // 6. Redraw relevant layers
            this.redraw(); // Global redraw
        },

        /**
         * Toggle visibility of a dropdown menu and close others
         * @param {string} menu_name - Name of the menu to toggle ('file', 'edit', etc)
         */
        toggle_dropdown_menu(menu_name) {
            // Define property name following the pattern "show_X_menu"
            const menu_prop = `show_${menu_name}_menu`;
            
            // Close all menus first
            this.close_all_dropdown_menus();
            
            // Toggle the requested menu
            if (menu_prop in this) {
                this[menu_prop] = !this[menu_prop];
            }
        },

        /**
         * Closes all dropdown menus in the application
         */
        close_all_dropdown_menus() {
            // Close all menu props that follow the naming convention
            if (this.show_file_menu) this.show_file_menu = false;
            if (this.show_edit_menu) this.show_edit_menu = false;
            if (this.show_image_menu) this.show_image_menu = false;
            if (this.show_view_menu) this.show_view_menu = false;
            if (this.show_about_menu) this.show_about_menu = false;
            if (this.show_mobile_menu) this.show_mobile_menu = false;
            if (this.show_user_menu) this.show_user_menu = false;
            // Any future menus would be added here
        },

        close_all_menus() {
            this.close_all_dropdown_menus();
            this.hide_context_menu();
        },

        // View menu functions
        toggle_fullscreen() {
            if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen().catch(err => {
                    console.log(`Error attempting to enable fullscreen: ${err.message}`);
                });
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                }
            }
        },

        fit_zoom_to_panel() {
            // Use currently selected panel or last selected panel as fallback
            let target_panel = this.selected_panel;
            if (!target_panel || target_panel.getAttr('is_global')) {
                target_panel = this.last_selected_panel;
            }
            
            if (!target_panel || target_panel.getAttr('is_global')) {
                this.show_notification_modal(this.get_t('view.no_panel_selected'), 'warning');
                return;
            }
            
            const bounds = this.calculate_polygon_bounds(target_panel.points);
            
            if (!bounds) return;
            
            // Get absolute center of panel using existing method
            const panel_center = this.get_panel_center(target_panel);
            
            // Get stage dimensions
            const stage_width = this.stage.width();
            const stage_height = this.stage.height();
            
            // Calculate zoom to fit panel with some padding
            const padding = 50;
            const zoom_x = (stage_width - padding * 2) / bounds.width;
            const zoom_y = (stage_height - padding * 2) / bounds.height;
            const new_zoom = Math.min(zoom_x, zoom_y, 2); // Max zoom of 2x
            
            // Calculate new position to center the panel
            const new_x = stage_width / 2 - panel_center.x * new_zoom;
            const new_y = stage_height / 2 - panel_center.y * new_zoom;
            
            // Update canvas_zoom state properly
            this.canvas_zoom = new_zoom;
            
            // Apply zoom and position using proper method
            this.stage.scale({ x: new_zoom, y: new_zoom });
            this.stage.position({ x: new_x, y: new_y });
            this.stage.batchDraw();
        },

        // About menu functions (placeholder functions)
        show_version_info() {
            this.show_popup_with_content(
                this.get_t('about.version_info_title'),
                this.get_t('about.version_info_content')
            );
        },

        show_privacy_policy() {
            // Open privacy policy in new tab
            const base_url = window.location.protocol + '//' + window.location.host + '/';
            window.open(base_url + 'privacy_policy.php', '_blank');
        },

        show_terms_of_use() {
            // Open terms of service in new tab
            const base_url = window.location.protocol + '//' + window.location.host + '/';
            window.open(base_url + 'terms_of_service.php', '_blank');
        },

        show_about_mikomic() {
            const about_content = `
                <div class="space-y-4 mb-3">
                    <div class="text-center">
                        <h3 class="text-xl font-bold text-white mb-3">MiKomic Comic Creator</h3>
                    </div>
                    
                    <div class="text-gray-300 space-y-3">
                        <p>${this.get_t('about.description')}</p>
                        
                        <p class="pt-5"><strong class="text-white">${this.get_t('about.key_features')}:</strong></p>
                        <ul class="list-disc list-inside ml-4 space-y-1">
                            <li>${this.get_t('about.feature_panel_system')}</li>
                            <li>${this.get_t('about.feature_balloon_tools')}</li>
                            <li>${this.get_t('about.feature_layer_editing')}</li>
                            <li>${this.get_t('about.feature_multilang')}</li>
                            <li>${this.get_t('about.feature_export')}</li>
                        </ul>

                        <p class="text-center text-sm text-gray-400 pt-4">
                            ${this.get_t('about.copyright')}
                        </p>
                    </div>
                </div>
            `;
            this.show_notification_modal(about_content, 'info');
        },

        show_contact_support() {
            const contact_content = `
                <div class="space-y-6">
                    <div class="text-center">
                        <h3 class="text-xl font-bold text-white mb-3">${this.get_t('contact.title')}</h3>
                        <p class="text-gray-300">${this.get_t('contact.subtitle')}</p>
                    </div>
                    
                    <div class="space-y-4">
                        <div class="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                            <div class="flex items-center gap-3 mb-2">
                                <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                                </svg>
                                <h4 class="text-white font-semibold">${this.get_t('contact.email_title')}</h4>
                            </div>
                            <p class="text-gray-300 text-sm mb-3">${this.get_t('contact.email_description')}</p>
                            <a href="mailto:support@mikomic.com" class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                                </svg>
                                support@mikomic.com
                            </a>
                        </div>
                        
                        <div class="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                            <div class="flex items-center gap-3 mb-2">
                                <svg class="w-5 h-5 text-indigo-400" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9554 2.4189-2.1568 2.4189Z"/>
                                </svg>
                                <h4 class="text-white font-semibold">${this.get_t('contact.discord_title')}</h4>
                            </div>
                            <p class="text-gray-300 text-sm mb-3">${this.get_t('contact.discord_description')}</p>
                            <a href="https://discord.com/channels/1395447202108805286/1395451368214495354" target="_blank" class="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                                </svg>
                                ${this.get_t('contact.discord_button')}
                            </a>
                        </div>
                    </div>
                    
                    <div class="text-center text-sm text-gray-400">
                        <p>${this.get_t('contact.response_time')}</p>
                    </div>
                </div>
            `;
            this.show_notification_modal(contact_content, 'info');
        },

        open_discord() {
            // Go to discord server
            const discord_url = 'https://discord.gg/5XzGUdhmTM'; // unlimited invite link
            window.open(discord_url, '_blank');
        },

        open_twitter() {
            // Go to discord server
            const url = 'https://x.com/Mikomic_Editor';
            window.open(url, '_blank');
        },

        open_bluesky() {
            // Placeholder - will open BlueSky link when implemented
            this.show_notification_modal(this.get_t('about.coming_soon'), 'info');
        },

        // Tutorials modal methods
        show_tutorials_modal() {
            // Open tutorials in new window instead of iframe due to X-Frame-Options restrictions
            window.open('https://mikomic.com/docs/', '_blank');
            this.close_all_menus();
        },

        toggle_mobile_menu() {
            this.close_all_menus();
            this.show_mobile_menu = !this.show_mobile_menu;
        },

        toggle_right_bar() {
            this.right_bar_collapsed = !this.right_bar_collapsed;

            if (this.selected_object_is_balloon)
            {
                //delay this 400ms
                setTimeout(() => {
                    this.update_balloon_toolbar_position(this.selected_object);
                }, 400);
            }

            //this.handle_window_resize(true);
        },

        check_mobile_view() {
            this.is_mobile_view = window.innerWidth < 768;
        },

        /**
         * Handles global clicks to determine if dropdown menus should be closed
         * @param {MouseEvent} event - The click event
         */
        handle_global_click_for_menus(event) {
            // Skip if no menus are open
            if (!this.show_file_menu && 
                !this.show_edit_menu && 
                !this.show_image_menu && 
                !this.show_view_menu && 
                !this.show_about_menu &&
                !this.show_user_menu) 
            {
                return;
            }
            
            // Check if click is outside menu buttons and dropdowns
            const top_bar = document.getElementById('top_bar');
            if (top_bar && !top_bar.contains(event.target)) {
                this.close_all_dropdown_menus();
            }
        },

        // Premium modal methods
        show_premium_modal(action, message) {
            // Special handling for registration-only features
            if (action === 'registered_only') {
                this.premium_modal.action = action;
                this.premium_modal.message = message || this.get_t('user.registration_required');
                this.premium_modal.target_tier = 'premium';
            } 
            // Special handling for dimension limits (Creator Pro users exceeding max dimensions)
            else if (action === 'dimension_limit') {
                this.premium_modal.action = action;
                this.premium_modal.message = message || this.get_t('premium.dimension_limit_exceeded');
                this.premium_modal.target_tier = 'creator_pro'; // Show as info, not upgrade
            }
            // Special handling for Creator Pro export suggestions
            else if (action === 'creator_pro_export') {
                this.premium_modal.action = action;
                this.premium_modal.message = message || this.get_t('premium.creator_pro_export_limit');
                this.premium_modal.target_tier = 'creator_pro';
            }
            else {
                // Determine target tier based on current user tier and action
                let target_tier = 'premium';
                
                // If user is already premium and trying to exceed premium limits, show creator_pro
                if (this.user_data && this.user_data.tier === 'premium') {
                    // Check if this is a creator_pro-specific limit
                    if (action === 'add_page' || action === 'max_pages_reached' || action === 'creator_pro_export') {
                        target_tier = 'creator_pro';
                    }
                }
                
                this.premium_modal.action = action;
                this.premium_modal.message = message || this.get_t('premium.feature_locked');
                this.premium_modal.target_tier = target_tier;
            }
            
            this.is_premium_modal_open = true;

            this.deselect_all();
            
            // Refresh lucide icons for the modal
            this.$nextTick(() => {
                this.refresh_lucide();
            });
        },

        close_premium_modal() {
            this.is_premium_modal_open = false;
            this.premium_modal.action = '';
            this.premium_modal.message = '';
            this.premium_modal.target_tier = 'premium';
            this.deselect_all();
            this.select_global_layers();
        },

        show_register_form() {
            // Close premium modal and redirect to registration
            this.close_premium_modal();
            this.show_register_modal();
        },

        // Get tier features for modal display
        get_tier_features(tier) {
            const base_features = {
                premium: [
                    { key: 'unlimited_panels', enabled: true },
                    { key: 'unlimited_layers', enabled: true },
                    { key: 'unlimited_balloons', enabled: true },
                    { key: 'high_res_export', enabled: true },
                    { key: 'copy_paste_styles', enabled: true },
                    { key: 'fast_autosave', enabled: true },
                    { key: 'pages_per_chapter', enabled: true, count: 12 }
                ],
                creator_pro: [
                    { key: 'unlimited_panels', enabled: true },
                    { key: 'unlimited_layers', enabled: true },
                    { key: 'unlimited_balloons', enabled: true },
                    { key: 'high_res_export', enabled: true },
                    { key: 'copy_paste_styles', enabled: true },
                    { key: 'fast_autosave', enabled: true },
                    { key: 'pages_per_chapter', enabled: true, count: 32 },
                    { key: 'ai_generation', enabled: this.feature_flags.ai_generation },
                    { key: 'ai_character_manager', enabled: this.feature_flags.ai_character_manager },
                    { key: 'ai_translation', enabled: this.feature_flags.ai_translation }
                ]
            };
            
            return base_features[tier] || [];
        },


        //! Welcome screen methods
        show_welcome_screen_view() {
            this.show_welcome_screen = true;
        },

        hide_welcome_screen_view() {
            this.show_welcome_screen = false;
        },

        show_recent_files_panel() {
            // For now, just hide the welcome screen to show the main interface
            // In the future, this could open a dedicated recent files panel
            this.hide_welcome_screen_view();
        },

        //! Templates system methods
        is_template_premium(template_id) {
            return this.premium_template_status[template_id] || false;
        },

        async load_recent_templates() {
 
            try {
                this.loading_templates = true;
                
                const formData = new FormData();
                formData.append('request', 'get_templates');
                formData.append('sort_by', 'first_4'); // New option for first 4 templates ordered by ID
                formData.append('limit', '4');
                
                const response = await axios.post('ajax.php', formData);
                
                if (response.data.success) {
                    this.templates = response.data.templates;
                    
                    // Load thumbnails for each template
                    for (const template of this.templates) {
                        await this.load_template_thumbnail(template);
                    }
                } else {
                    console.error('Error loading recent templates:', response.data.message);
                    this.templates = [];
                }
            } catch (error) {
                console.error('Error loading recent templates:', error);
                this.templates = [];
            } finally {
                this.loading_templates = false;
            }
        },

        async load_template_thumbnail(template) {
            try {
                const template_url = `data/templates/${template.id}.mkc`;
                const file_response = await fetch(template_url);
                if (file_response.ok) {
                    const arrayBuffer = await file_response.arrayBuffer();
                    
                    // Decode MKC file
                    let mkc_data = null;
                    try {
                        if (typeof MessagePack !== 'undefined') {
                            mkc_data = MessagePack.decode(new Uint8Array(arrayBuffer));
                        } else {
                            console.log('MessagePack library not available');
                        }
                    } catch (decode_error) {
                        console.log(`Could not decode ${template.id}.mkc:`, decode_error);
                    }
                    
                    // Extract thumbnail
                    if (mkc_data && mkc_data.metadata && mkc_data.metadata.document_thumbnail) {
                        template.thumbnail = `data:image/png;base64,${mkc_data.metadata.document_thumbnail}`;
                    } else if (mkc_data && mkc_data.document_thumbnail) {
                        template.thumbnail = `data:image/png;base64,${mkc_data.document_thumbnail}`;
                    } else if (mkc_data && mkc_data.gallery_item && mkc_data.gallery_item.thumbnail_base64) {
                        template.thumbnail = `data:image/png;base64,${mkc_data.gallery_item.thumbnail_base64}`;
                    } else {
                        if (mkc_data && mkc_data.metadata) {
                            console.log(`Metadata keys for ${template.id}:`, Object.keys(mkc_data.metadata));
                        }
                        template.thumbnail = null;
                    }
                } else {
                    console.log(`Failed to fetch ${template.id}.mkc`);
                    template.thumbnail = null;
                }
            } catch (error) {
                console.log(`Failed to load thumbnail for welcome screen ${template.id}:`, error);
                template.thumbnail = null;
            }
        },

        async load_recent_templates_with_thumbnails() {
            const templates_grid = document.getElementById('welcome_templates_grid');
            if (!templates_grid || this.templates.length === 0) return;

            // Clear existing content
            templates_grid.innerHTML = '';

            // Show first 4 templates (2x2 grid)
            const display_templates = this.templates.slice(0, 4);

            for (const template of display_templates) {
                const template_card = document.createElement('div');
                template_card.className = 'group bg-gradient-to-br from-gray-700/30 to-gray-800/30 rounded-lg p-2 border border-gray-600/30 hover:border-purple-500/50 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-purple-500/10 hover:scale-[1.02] flex flex-col';
                template_card.onclick = () => {
                    this.load_template(template.id);
                };

                // Default thumbnail
                let thumbnail_html = `<div class="flex flex-col items-center justify-center text-gray-400 h-full">
                    <i data-lucide="image" class="w-4 h-4 mb-1"></i>
                    <span class="text-xs">${this.get_t('welcome.template_preview')}</span>
                </div>`;

                // Try to load thumbnail from MKC file
                try {
                    const template_url = `data/templates/${template.id}.mkc`;
                    const file_response = await fetch(template_url);
                    if (file_response.ok) {
                        const arrayBuffer = await file_response.arrayBuffer();
                        
                        // Decode MKC file
                        let mkc_data = null;
                        try {
                            if (typeof MessagePack !== 'undefined') {
                                mkc_data = MessagePack.decode(new Uint8Array(arrayBuffer));
                            } else {
                                console.log('MessagePack library not available');
                            }
                        } catch (decode_error) {
                            console.log(`Could not decode ${template.id}.mkc:`, decode_error);
                        }
                        
                        // Extract thumbnail
                        if (mkc_data && mkc_data.metadata && mkc_data.metadata.document_thumbnail) {
                            thumbnail_html = `<img src="data:image/png;base64,${mkc_data.metadata.document_thumbnail}" alt="${template.name}" class="w-full h-full object-cover rounded" />`;
                        } else if (mkc_data && mkc_data.document_thumbnail) {
                            thumbnail_html = `<img src="data:image/png;base64,${mkc_data.document_thumbnail}" alt="${template.name}" class="w-full h-full object-cover rounded" />`;
                        } else if (mkc_data && mkc_data.gallery_item && mkc_data.gallery_item.thumbnail_base64) {
                            thumbnail_html = `<img src="data:image/png;base64,${mkc_data.gallery_item.thumbnail_base64}" alt="${template.name}" class="w-full h-full object-cover rounded" />`;
                        } else {
                            // console.log(`No thumbnail found for welcome screen ${template.id}, mkc_data structure:`, mkc_data ? Object.keys(mkc_data) : 'null');
                            // if (mkc_data && mkc_data.metadata) {
                            //     console.log(`Metadata keys for ${template.id}:`, Object.keys(mkc_data.metadata));
                            // }
                        }
                    }
                } catch (error) {
                    console.log(`Failed to load thumbnail for welcome screen ${template.id}:`, error);
                }

                template_card.innerHTML = `
                    <div class="aspect-[4/3] bg-gray-600/50 rounded flex items-center justify-center overflow-hidden mb-1">
                        ${thumbnail_html}
                    </div>
                    <div class="space-y-0.5 flex-grow">
                        <h4 class="font-medium text-white group-hover:text-purple-300 transition-colors text-xs leading-tight truncate">${template.name}</h4>
                        <p class="text-xs text-gray-400 truncate">${template.description || this.get_t('welcome.template_preview')}</p>
                    </div>
                `;

                templates_grid.appendChild(template_card);
            }

            // Refresh lucide icons
            this.refresh_lucide();
        },

        async load_all_templates() {
            try {
                this.loading_templates = true;
                
                const formData = new FormData();
                formData.append('request', 'get_templates');
                formData.append('sort_by', 'all');
                
                const response = await axios.post('ajax.php', formData);

                if (response.data.success) {
                    this.templates = response.data.templates;
                    
                    // Load thumbnails for each template
                    for (const template of this.templates) {
                        await this.load_template_thumbnail(template);
                    }
                } else {
                    console.error('Error loading all templates:', response.data.message);
                    this.templates = [];
                }
            } catch (error) {
                console.error('Error loading all templates:', error);
                this.templates = [];
            } finally {
                this.loading_templates = false;
            }
        },

        show_templates_browser() {
            this.show_templates_modal = true;
            this.load_all_templates();
            
            this.$nextTick(() => {
                this.refresh_lucide();
            });
        },

        close_templates_browser() {
            this.show_templates_modal = false;
        },

        async load_template(template_id) {
            try {
                this.status_message = this.get_t('welcome.loading_template');
                
                // Load the template file directly using fetch
                const template_url = `data/templates/${template_id}.mkc`;
                
                const response = await fetch(template_url);
                if (!response.ok) {
                    throw new Error('Template file not found');
                }
                
                const arrayBuffer = await response.arrayBuffer();
                
                // Create a fake File object for the load_document_from_file function
                const blob = new Blob([arrayBuffer], { type: 'application/x-mkcomic' });
                const file = new File([blob], `${template_id}.mkc`, { type: 'application/x-mkcomic' });
                
                // Close any open modals
                this.close_templates_browser();
                this.close_popup();
                
                // Use the normal file loading function
                this.load_document_from_file(file);
                
                // Reset file state to treat this as a new document
                this.current_file_handle = null;
                this.current_file_path = null;
                this.document_has_unsaved_changes = false;
                
            } catch (error) {
                console.error('Error loading template:', error);
                this.show_error_message(this.get_t('error.title'), this.get_t('error.failed_to_load_template'));
                this.status_message = this.get_t('status.error_loading');
            }
        },

        async load_templates_in_dialog() {
            const templates_loading = document.getElementById('templates_loading');
            const templates_grid = document.getElementById('templates_grid');
            const templates_empty = document.getElementById('templates_empty');

            if (!templates_loading || !templates_grid || !templates_empty) return;

            // Show loading state
            templates_loading.style.display = 'flex';
            templates_grid.style.display = 'none';
            templates_empty.style.display = 'none';

            try {
                const formData = new FormData();
                formData.append('request', 'get_templates');
                formData.append('sort_by', 'all');
                
                const response = await axios.post('ajax.php', formData);

                if (response.data.success && response.data.templates.length > 0) {
                    // Clear existing content
                    templates_grid.innerHTML = '';

                    // Generate template cards with thumbnail extraction
                    for (const template of response.data.templates) {
                        const template_card = document.createElement('div');
                        template_card.className = 'group bg-gradient-to-br from-gray-700/30 to-gray-800/30 rounded-xl p-4 border border-gray-600/30 hover:border-purple-500/50 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-purple-500/10 flex flex-col relative';
                        
                        // Add opacity class if template is premium and user is not premium
                        if (this.is_template_premium(template.id) && !this.user_data.is_premium) {
                            template_card.className += ' opacity-60';
                        }
                        
                        template_card.onclick = () => {
                            // Check if template is premium and user is not premium
                            if (this.is_template_premium(template.id) && !this.user_data.is_premium) {
                                this.close_popup();
                                this.show_premium_modal('premium', this.get_t('premium.premium_required'));
                                return;
                            }
                            
                            this.close_popup();
                            this.load_template(template.id);
                        };

                        // Try to load thumbnail by reading the file
                        let thumbnail_html = `<div class="flex flex-col items-center justify-center text-gray-400 h-full">
                            <i data-lucide="image" class="w-8 h-8 mb-2"></i>
                            <span class="text-sm">${this.get_t('welcome.template_preview')}</span>
                        </div>`;

                        try {
                            const template_url = `data/templates/${template.id}.mkc`;
                            const file_response = await fetch(template_url);
                            if (file_response.ok) {
                                const arrayBuffer = await file_response.arrayBuffer();
                                
                                // Decode MKC file using the same method as document loading
                                let mkc_data = null;
                                try {
                                    if (typeof MessagePack !== 'undefined') {
                                        mkc_data = MessagePack.decode(new Uint8Array(arrayBuffer));
                                    } else {
                                        console.log('MessagePack library not available');
                                    }
                                } catch (decode_error) {
                                    console.log(`Could not decode ${template.id}.mkc:`, decode_error);
                                }
                                
                                // Extract thumbnail if found
                                if (mkc_data && mkc_data.metadata && mkc_data.metadata.document_thumbnail) {
                                    thumbnail_html = `<img src="data:image/png;base64,${mkc_data.metadata.document_thumbnail}" alt="${template.name}" class="w-full h-full object-contain rounded-lg" />`;
                                } else if (mkc_data && mkc_data.document_thumbnail) {
                                    thumbnail_html = `<img src="data:image/png;base64,${mkc_data.document_thumbnail}" alt="${template.name}" class="w-full h-full object-contain rounded-lg" />`;
                                } else if (mkc_data && mkc_data.gallery_item && mkc_data.gallery_item.thumbnail_base64) {
                                    thumbnail_html = `<img src="data:image/png;base64,${mkc_data.gallery_item.thumbnail_base64}" alt="${template.name}" class="w-full h-full object-contain rounded-lg" />`;
                                } else {
                                    // console.log(`No thumbnail found for dialog ${template.id}, mkc_data structure:`, mkc_data ? Object.keys(mkc_data) : 'null');
                                    // if (mkc_data && mkc_data.metadata) {
                                    //     console.log(`Metadata keys for dialog ${template.id}:`, Object.keys(mkc_data.metadata));
                                    // }
                                }
                            }
                        } catch (error) {
                            console.log(`Failed to load thumbnail for dialog ${template.id}:`, error);
                        }

                        template_card.innerHTML = `
                            <!-- Premium Badge -->
                            ${this.is_template_premium(template.id) ? `
                                <div class="absolute top-2 right-2 z-10">
                                    <div class="flex items-center justify-center w-5 h-5 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full shadow-lg">
                                        <i data-lucide="sparkles" class="w-2.5 h-2.5 text-white"></i>
                                    </div>
                                </div>
                            ` : ''}

                            <!-- Premium Overlay (for non-premium users) -->
                            ${this.is_template_premium(template.id) && !this.user_data.is_premium ? `
                                <div class="absolute inset-0 bg-black/40 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-20">
                                    <div class="text-center">
                                        <i data-lucide="lock" class="w-5 h-5 text-amber-400 mx-auto mb-1"></i>
                                        <p class="text-xs text-amber-400 font-medium">${this.get_t('premium.required')}</p>
                                    </div>
                                </div>
                            ` : ''}

                            <div class="aspect-[3/4] bg-gray-600/30 rounded-lg flex items-center justify-center overflow-hidden mb-3">
                                ${thumbnail_html}
                            </div>
                            <div class="text-center">
                                <h4 class="font-medium text-white group-hover:text-purple-300 transition-colors text-sm leading-tight truncate">${template.name}</h4>
                            </div>
                        `;

                        templates_grid.appendChild(template_card);
                    }

                    // Refresh lucide icons in the new content
                    this.refresh_lucide();

                    // Show templates grid
                    templates_loading.style.display = 'none';
                    templates_grid.style.display = 'grid';
                    templates_grid.className = templates_grid.className.replace('hidden', '');
                } else {
                    // Show empty state
                    templates_loading.style.display = 'none';
                    templates_empty.style.display = 'flex';
                    templates_empty.className = templates_empty.className.replace('hidden', '');
                }
            } catch (error) {
                console.error('Error loading templates in dialog:', error);
                // Show empty state on error
                templates_loading.style.display = 'none';
                templates_empty.style.display = 'flex';
                templates_empty.className = templates_empty.className.replace('hidden', '');
            }
        },

        // User menu toggle
        toggle_user_menu() {
            this.close_all_dropdown_menus();
            this.show_user_menu = !this.show_user_menu;
        },

        // Show subscription success modal (no X button, forces page refresh)
        show_subscription_success_modal(message, type = 'success') {
            this.subscription_success_modal.message = message;
            this.subscription_success_modal.type = type;
            this.subscription_success_modal.show = true;
        },

        // Hide subscription success modal and refresh page
        hide_subscription_success_modal() {
            this.subscription_success_modal.show = false;
            this.subscription_success_modal.message = '';
            this.subscription_success_modal.type = 'success';
            // Always refresh page when closing this modal
            window.location.reload();
        }, 

    },

    mounted() {
        if (this.comic_mode != true) {
            return;
        }

        if (!this.preferences.ui) {
            this.$set(this.preferences, 'ui', {});
        }
        if (!this.preferences.ui.panel_collapsed) {
            this.$set(this.preferences.ui, 'panel_collapsed', {});
        }
        
        // Important: Clone the stored preferences to panel_collapsed
        this.panel_collapsed = JSON.parse(JSON.stringify(this.preferences.ui.panel_collapsed));
        
        // Initialize mobile view detection
        this.check_mobile_view();
        window.addEventListener('resize', this.check_mobile_view);
        
        // Apply initial open state to all visible panels
        this.$nextTick(() => {
            // We need to find all panel content elements and initialize them
            const panel_contents = document.querySelectorAll('[data-panel-id]');
            panel_contents.forEach(panel => {
                const panel_id = panel.getAttribute('data-panel-id'); 
                if (!this.panel_collapsed[panel_id]) {
                    // If panel is not collapsed, add the open class
                    panel.classList.add('open');
                }
            });
        });
        
        // Load recent templates if on welcome screen
        if (this.show_welcome_screen) {
            this.load_recent_templates();
        }
    },
};